// inviteChannel — pure helpers for the in-call "Add participant" flow:
// WHO can be invited, and which EXISTING channel the invitation is sent to.
//
// Root cause this module exists for: the overlay used to call
// channel("messaging", { members: [a, b] }) — no explicit id.
// Stream does NOT derive the sorted-pair id from a member list there; a
// members-only create mints a DISTINCT channel with a synthetic id
// (`!members-<hash>`) — a shadow thread parallel to every canonical
// `direct_*` / pitch channel the backend creates (chat_channel_ids.py).
// Invitations are therefore routed ONLY into channels that already exist —
// never a client-side create (the create permission is being removed from
// the messaging channel type server-side, so a create would 403 anyway).

import { stripMessagingPrefix } from "../../../utils/chatTimer";
import { isExpiredChannel } from "../archiveState";

// Mirror of the backend's chat_channel_ids.py: the canonical 1:1 DM id is
// `direct_<a>_<b>` over `sorted([str(a), str(b)])` — a STRING sort, so
// ("9", "10") -> "direct_10_9", NOT "direct_9_10".
export const buildDirectChannelId = (a, b) => {
  const pair = [String(a), String(b)].sort();
  return `direct_${pair[0]}_${pair[1]}`;
};

// A channel can surface invitees / host an invitation only while it is
// ACTIVE: not frozen (closed) and not timer-expired — the same predicates
// the composer and header use (CustomMessageInput.js / archiveState.js).
export const isActiveChannel = (channel) =>
  Boolean(channel) &&
  channel?.data?.frozen !== true &&
  !isExpiredChannel(channel);

// The chat thread the call was started from. Primary source: the
// `channel_cid` CustomChatHeader stamps into the call's custom data at
// getOrCreate (travels to everyone who joins). Fallback: the call-id
// convention those same handlers use — `${audio|video}-${channel.id}-
// ${Date.now()}`. The greedy group keeps hyphens INSIDE the channel id;
// only the trailing timestamp is stripped.
const CALL_ID_RE = /^(?:audio|video)-(.+)-\d+$/;

export const originChannelIdOfCall = (call) => {
  const stamped = call?.state?.custom?.channel_cid;
  if (stamped) return stripMessagingPrefix(stamped);
  const match = CALL_ID_RE.exec(String(call?.id || ""));
  return match ? match[1] : null;
};

// Build the invitable-people list from the viewer's OWN loaded channels:
// active 1:1 threads only, one row per counterpart, most recent shared
// channel wins (`channels` arrive sorted by last_message_at desc). Each row
// carries its source channel — the delivery target of last resort below.
export const buildInviteCandidates = (channels, selfId) => {
  const seen = new Set();
  const candidates = [];
  for (const channel of channels || []) {
    if (!isActiveChannel(channel)) continue;
    const memberIds = Object.keys(channel.state?.members || {});
    // 1:1 only — skip groups
    if (memberIds.length !== 2) continue;
    const otherId = memberIds.find((id) => id !== selfId);
    if (!otherId || seen.has(otherId)) continue;
    seen.add(otherId);
    const member = channel.state.members[otherId];
    candidates.push({
      id: otherId,
      name: member?.user?.name || otherId,
      role: member?.user?.role || "",
      image: member?.user?.image || null,
      channel,
    });
  }
  return candidates;
};

// Which EXISTING channel receives the call_invitation for `candidate`:
//   1. The call's origin thread, when the invitee is one of its members
//      (group-call case / re-inviting the DM counterpart) — the invitation
//      stays in the thread the call context lives in.
//   2. Their canonical DM `direct_<a>_<b>`, when it is among the loaded
//      channels.
//   3. The channel the candidate was listed from (their most recent shared
//      active 1:1 thread).
// Nothing is ever created: the origin id is known to exist (the call was
// started from it); 2. and 3. are channel objects out of queryChannels.
export const resolveInviteChannel = async ({
  chatClient,
  call,
  candidate,
  channels,
}) => {
  const originId = originChannelIdOfCall(call);
  if (originId) {
    try {
      const origin = chatClient.channel("messaging", originId);
      await origin.watch();
      if (origin.state?.members?.[candidate.id]) return origin;
    } catch (err) {
      // Origin thread not readable for us (e.g. an invited guest, who is
      // not a member of the thread the call started from) — fall through.
      console.error("Origin channel unavailable for call invite:", err);
    }
  }
  const dmId = buildDirectChannelId(chatClient.userID, candidate.id);
  const dm = (channels || []).find((c) => c?.id === dmId);
  if (dm) return dm;
  return candidate.channel;
};
